/**
 * Inject Splide autoplay controls (progress pill, replay, play/pause) into
 * autoplay-enabled carousels.
 *
 * The PaceCore carousel block mounts its own Splide instances from plugin JS,
 * while the child theme mounts `.carousel-dynamic` instances from `main_v5.js`.
 * This helper intentionally runs before either mount path whenever possible so
 * Splide can discover the `.splide__toggle` button and wire the play/pause
 * behavior through its built-in Autoplay component.
 *
 * The autoplay "fill timer" is rendered as a dedicated pill that sits after the
 * pagination dots, so the dots themselves behave like normal Splide pagination
 * (the active dot is simply a solid red circle). The pill fill is a CSS
 * animation that we restart whenever Splide advances a slide or (re)starts
 * autoplay, which keeps it in sync with Splide without depending on the Splide
 * instance API (the custom block does not expose it).
 *
 * @since 1.0.0
 */

const pacecoreCarouselSelector = '.wp-block-pacecore-blocks-carousel.splide[data-splide]';
const themeAutoplayCarouselSelector = '.carousel-dynamic.carousel-autoplay';
const controlsSelector = '.carousel-autoplay-controls';
const progressBarSelector = '.carousel-autoplay-progress__bar';
const toggleSelector = '.carousel-autoplay-toggle';
const replaySelector = '.carousel-autoplay-replay';
const controlsClass = 'carousel-autoplay-controls';
const progressTrackClass = 'carousel-autoplay-progress';
const progressBarClass = 'carousel-autoplay-progress__bar';
const progressBarRunningClass = 'is-running';
const replayButtonClass = 'carousel-autoplay-replay';
const replayIconClass = 'carousel-autoplay-replay__icon';
const toggleButtonClass = 'splide__toggle carousel-autoplay-toggle';
const playIconClass = 'splide__toggle__play carousel-autoplay-toggle__icon carousel-autoplay-toggle__icon--play';
const pauseIconClass = 'splide__toggle__pause carousel-autoplay-toggle__icon carousel-autoplay-toggle__icon--pause';
const autoplayCarouselRootSelector = `${pacecoreCarouselSelector}, ${themeAutoplayCarouselSelector}`;
const autoplayScopeSelector = '.carousel-autoplay';
const mobileMediaQuery = '(max-width: 991px)';
const mediumMediaQuery = '(min-width: 992px) and (max-width: 1229.98px)';
const mobileNavWidthPx = 343;
const mobileNavSideInsetPx = 32;
const desktopToggleRightPx = 90;
const contentEdgeInsetPx = 20;
const resizeDebounceMs = 150;
const defaultAutoplayIntervalMs = 5000;

let alignmentListenersBound = false;
let lifecycleListenersBound = false;
const observedActiveDotScopes = new WeakSet();

/**
 * Parse a carousel root's saved Splide options.
 *
 * @param {Element} carousel Carousel root element with a data-splide attribute.
 * @returns {Object|null} Parsed Splide options or null when parsing fails.
 */
function getSavedSplideOptions(carousel) {
	try {
		return JSON.parse(carousel.getAttribute('data-splide') || '{}');
	} catch {
		/*
		 * A malformed `data-splide` value means we cannot know whether the block
		 * actually opted into autoplay. Skip that carousel instead of injecting a
		 * control that would not be connected to a running timer.
		 */
		return null;
	}
}

/**
 * Determine whether a PaceCore carousel block has autoplay enabled.
 *
 * @param {Element} carousel Carousel root element.
 * @returns {boolean} True when the saved Splide config enables autoplay.
 */
function isPacecoreAutoplayCarousel(carousel) {
	const options = getSavedSplideOptions(carousel);

	return Boolean(options?.autoplay);
}

/**
 * Determine whether a carousel belongs to a section that should display the
 * Figma-style autoplay control.
 *
 * @param {Element} carousel Carousel root element.
 * @returns {boolean} True when the carousel is inside a carousel-autoplay block.
 */
function hasAutoplayPresentationScope(carousel) {
	return Boolean(carousel.closest(autoplayScopeSelector));
}

/**
 * Resolve the autoplay presentation scope for an element.
 *
 * @param {Element|null|undefined} element
 * @returns {Element|null}
 */
function resolveAutoplayScope(element) {
	if (!element) {
		return null;
	}

	if (element.matches(autoplayScopeSelector)) {
		return element;
	}

	return element.closest(autoplayScopeSelector);
}

/**
 * Resolve the element that owns the pagination CSS custom properties.
 *
 * @param {Element} scope
 * @returns {Element}
 */
function resolvePaginationScope(scope) {
	if (scope.classList.contains('carousel-pagination-in-slide')) {
		return scope;
	}

	return scope.querySelector('.carousel-pagination-in-slide') || scope;
}

/**
 * Resolve the autoplay interval (ms) so the CSS fill animation matches the
 * Splide timer for this carousel.
 *
 * @param {Element} carousel Carousel root element.
 * @returns {number} Interval in milliseconds.
 */
function getAutoplayIntervalMs(carousel) {
	if (carousel.matches(pacecoreCarouselSelector)) {
		const options = getSavedSplideOptions(carousel);
		const interval = Number(options?.interval);

		if (Number.isFinite(interval) && interval > 0) {
			return interval;
		}
	}

	return defaultAutoplayIntervalMs;
}

/**
 * Position the controls cluster using a viewport coordinate, anchored by right.
 *
 * @param {HTMLElement} controls
 * @param {number} viewportTop
 * @param {number} rightPx
 */
function setControlsViewportPositionWithRight(controls, viewportTop, rightPx) {
	const offsetParent = controls.offsetParent;

	if (!offsetParent) {
		return;
	}

	const offsetParentRect = offsetParent.getBoundingClientRect();

	controls.style.position = 'absolute';
	controls.style.bottom = '';
	controls.style.left = '';
	controls.style.top = `${viewportTop - offsetParentRect.top + offsetParent.scrollTop}px`;
	controls.style.right = `${rightPx}px`;
}

/**
 * Position the controls cluster so its right edge aligns to a viewport x,
 * anchored by the CSS `right` property relative to the offset parent.
 *
 * @param {HTMLElement} controls
 * @param {number} viewportTop
 * @param {number} viewportRight Viewport x the controls' right edge should meet.
 */
function setControlsViewportPositionToRightEdge(controls, viewportTop, viewportRight) {
	const offsetParent = controls.offsetParent;

	if (!offsetParent) {
		return;
	}

	const offsetParentRect = offsetParent.getBoundingClientRect();

	setControlsViewportPositionWithRight(
		controls,
		viewportTop,
		offsetParentRect.right - viewportRight
	);
}

/**
 * Resolve the right edge of the carousel's visible card (`.wp-block-media-text`).
 *
 * @param {Element} scope
 * @returns {number|null} Viewport x of the carousel card's right edge, or null.
 */
function getCarouselContentRight(scope) {
	/*
	 * The visible carousel edge is the alignwide card (`.wp-block-media-text`),
	 * not the full-width `.splide` root. Anchoring to the card keeps the controls
	 * flush with the carousel on every breakpoint, since the card's gutters
	 * change with the layout while a fixed inset from `.splide` does not. The
	 * active slide's card is preferred so its measured right edge reflects the
	 * on-screen (untranslated) position, with any card as a pre-mount fallback.
	 */
	const card =
		scope.querySelector('.splide__slide.is-active .wp-block-media-text') ||
		scope.querySelector('.wp-block-media-text');

	if (!card) {
		return null;
	}

	return card.getBoundingClientRect().right;
}

/**
 * Vertically center the controls cluster against the pagination row and pin its
 * right edge to the carousel card's right edge.
 *
 * @param {Element} scope
 * @param {HTMLElement} controls
 * @param {Element} pagination
 */
function positionControls(scope, controls, pagination) {
	const paginationRect = pagination.getBoundingClientRect();
	const controlsHeight = controls.offsetHeight || 32;
	const viewportTop =
		paginationRect.top + (paginationRect.height - controlsHeight) / 2;
	const contentRight = getCarouselContentRight(scope);

	if (contentRight !== null) {
		setControlsViewportPositionToRightEdge(
			controls,
			viewportTop,
			contentRight - contentEdgeInsetPx
		);
		return;
	}

	/* Fallback before the slides are measurable: inset from the scope's edge. */
	setControlsViewportPositionWithRight(controls, viewportTop, desktopToggleRightPx);
}

/**
 * Align the controls cluster with the pagination row for one carousel scope.
 *
 * @param {Element|null|undefined} scopeRoot
 */
function alignCarouselAutoplayToggleNow(scopeRoot) {
	const scope = resolveAutoplayScope(scopeRoot);

	if (!scope) {
		return;
	}

	const controls = scope.querySelector(controlsSelector);
	const pagination = scope.querySelector('.splide__pagination');

	if (!controls || !pagination) {
		return;
	}

	const paginationScope = resolvePaginationScope(scope);
	const isMobile = window.matchMedia(mobileMediaQuery).matches;
	const isPagPositioned = paginationScope.classList.contains('is-pagination-positioned');

	if (isMobile && isPagPositioned) {
		const navWidth = Math.min(
			mobileNavWidthPx,
			Math.max(scope.clientWidth - mobileNavSideInsetPx, 0)
		);
		const navLeft = (scope.clientWidth - navWidth) / 2;

		paginationScope.style.setProperty('--carousel-autoplay-nav-width', `${navWidth}px`);
		paginationScope.style.setProperty('--carousel-autoplay-nav-left', `${navLeft}px`);

		requestAnimationFrame(() => {
			positionControls(scope, controls, pagination);
		});

		return;
	}

	paginationScope.style.removeProperty('--carousel-autoplay-nav-width');
	paginationScope.style.removeProperty('--carousel-autoplay-nav-left');

	positionControls(scope, controls, pagination);
}

/**
 * Align the autoplay controls with the pagination row for one carousel scope.
 *
 * Exported for `carousel-pagination-in-slide.js`, which repositions the
 * pagination and needs the controls cluster to follow it.
 *
 * @param {Element|null|undefined} scopeRoot Carousel-autoplay wrapper or descendant.
 */
export function alignCarouselAutoplayToggle(scopeRoot) {
	requestAnimationFrame(() => {
		alignCarouselAutoplayToggleNow(scopeRoot);
	});
}

/**
 * Align all carousel scopes, or a single scope when provided.
 *
 * @param {Element|null|undefined} scopeRoot
 */
function scheduleAutoplayToggleAlign(scopeRoot) {
	if (scopeRoot) {
		alignCarouselAutoplayToggle(scopeRoot);
		return;
	}

	document.querySelectorAll(autoplayScopeSelector).forEach((scope) => {
		alignCarouselAutoplayToggle(scope);
	});
}

/**
 * Bind resize and Splide listeners once for controls alignment.
 */
function bindAutoplayToggleAlignmentListeners() {
	if (alignmentListenersBound) {
		return;
	}

	alignmentListenersBound = true;

	let resizeTimeout;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => scheduleAutoplayToggleAlign(null), resizeDebounceMs);
	});

	window.matchMedia(mobileMediaQuery).addEventListener('change', () => {
		scheduleAutoplayToggleAlign(null);
	});

	window.matchMedia(mediumMediaQuery).addEventListener('change', () => {
		scheduleAutoplayToggleAlign(null);
	});

	document.addEventListener('splide:resized', (e) => {
		scheduleAutoplayToggleAlign(resolveAutoplayScope(e.target));
	});

	window.addEventListener('load', () => {
		scheduleAutoplayToggleAlign(null);
	});
}

/**
 * Restart the CSS fill animation on the autoplay progress pill.
 *
 * The pill is a single persistent element, so (unlike the previous per-dot
 * fill) the animation does not restart on its own when Splide advances. We
 * remove the running class, force a reflow, then re-add it so the keyframe
 * plays again from zero, keeping the visual fill aligned with the new slide.
 *
 * @param {Element} scope Carousel-autoplay wrapper.
 */
function restartProgress(scope) {
	if (scope.classList.contains('is-autoplay-ended')) {
		return;
	}

	const bar = scope.querySelector(progressBarSelector);

	if (!bar) {
		return;
	}

	bar.classList.remove(progressBarRunningClass);
	// Force a reflow so the browser treats the next class add as a fresh run.
	void bar.offsetWidth;
	bar.classList.add(progressBarRunningClass);
}

/**
 * Create the dedicated autoplay progress pill.
 *
 * @returns {HTMLSpanElement} The pill wrapper containing the fill bar.
 */
function createProgressPill() {
	const track = document.createElement('span');
	const bar = document.createElement('span');

	track.className = progressTrackClass;
	track.setAttribute('aria-hidden', 'true');

	bar.className = progressBarClass;

	track.append(bar);

	return track;
}

/**
 * Append the progress pill as the last child of the carousel's pagination.
 *
 * Splide builds (and can later rebuild) the pagination during and after mount,
 * which happens after this module injects its controls. We therefore observe
 * the scope subtree and keep the pill as the pagination's final child so it
 * always renders right after the dots, even if Splide regenerates the
 * pagination on a breakpoint change.
 *
 * @param {Element} scope Carousel-autoplay wrapper.
 */
function attachProgressToPagination(scope) {
	const pill = createProgressPill();

	const ensurePlaced = () => {
		const pagination = scope.querySelector('.splide__pagination');

		if (pagination && pagination.lastElementChild !== pill) {
			pagination.append(pill);
		}
	};

	ensurePlaced();

	if (typeof window.MutationObserver === 'undefined') {
		return;
	}

	const observer = new MutationObserver(ensurePlaced);

	observer.observe(scope, { childList: true, subtree: true });
}

/**
 * Create the replay button that restarts autoplay from the first slide.
 *
 * @returns {HTMLButtonElement} Button ready to insert into the controls cluster.
 */
function createReplayButton() {
	const button = document.createElement('button');
	const icon = document.createElement('span');

	button.type = 'button';
	button.className = replayButtonClass;
	button.setAttribute('aria-label', 'Replay autoplay');

	icon.className = replayIconClass;
	icon.setAttribute('aria-hidden', 'true');

	button.append(icon);

	return button;
}

/**
 * Create the Splide-compatible play/pause toggle button.
 *
 * The toggle is intentionally created without `is-active`; Splide adds that
 * class when it starts autoplay, which is also our cue to start the fill pill.
 * Starting without it avoids the pill animating before the carousel is actually
 * playing (PaceCore carousels only start once scrolled into view).
 *
 * @returns {HTMLButtonElement} Button Splide can wire to its Autoplay component.
 */
function createToggleButton() {
	const button = document.createElement('button');
	const playIcon = document.createElement('span');
	const pauseIcon = document.createElement('span');

	button.type = 'button';
	button.className = toggleButtonClass;
	button.setAttribute('aria-label', 'Pause autoplay');

	playIcon.className = playIconClass;
	playIcon.setAttribute('aria-hidden', 'true');

	pauseIcon.className = pauseIconClass;
	pauseIcon.setAttribute('aria-hidden', 'true');

	button.append(playIcon, pauseIcon);

	return button;
}

/**
 * Build the controls cluster: replay, then play/pause.
 *
 * The progress pill is not part of this cluster; it is appended to the Splide
 * pagination instead (see `attachProgressToPagination`).
 *
 * @returns {HTMLDivElement} The controls container.
 */
function createControls() {
	const controls = document.createElement('div');

	controls.className = controlsClass;
	controls.append(createReplayButton(), createToggleButton());

	return controls;
}

/**
 * Determine whether a carousel already owns a controls cluster.
 *
 * Nested carousels are uncommon, but this keeps the injection logic scoped to
 * the carousel being processed so a child carousel control cannot make the
 * parent carousel look initialized.
 *
 * @param {Element} carousel Carousel root element.
 * @returns {boolean} True when this carousel already owns controls.
 */
function hasOwnControls(carousel) {
	return Array.from(carousel.querySelectorAll(controlsSelector)).some((controls) => {
		return controls.closest(autoplayCarouselRootSelector) === carousel;
	});
}

/**
 * Mirror Splide's play/pause state onto the carousel scope.
 *
 * Splide's Autoplay component toggles the `is-active` class on the
 * `.splide__toggle` button whenever the carousel plays or pauses. We cannot
 * call Splide directly on the custom block, but we can observe that DOM change
 * and mirror it onto the scope so the CSS pill freezes while paused
 * (`animation-play-state: paused`) and resumes from the same point on play.
 *
 * The fill is only started here on the very first play; subsequent pause/resume
 * cycles must not restart it (that would snap the fill back to zero). Genuine
 * slide changes are handled by `observeActiveDotChanges`.
 *
 * @param {Element} scope Carousel-autoplay wrapper.
 * @param {HTMLButtonElement} toggle Injected toggle button.
 */
function observeAutoplayToggleState(scope, toggle) {
	let hasStarted = false;

	const syncState = () => {
		const isPlaying = toggle.classList.contains('is-active');

		scope.classList.toggle('is-autoplay-paused', !isPlaying);

		if (isPlaying && !hasStarted) {
			hasStarted = true;
			restartProgress(scope);
		}
	};

	syncState();

	const observer = new MutationObserver(syncState);

	observer.observe(toggle, { attributes: true, attributeFilter: ['class'] });
}

/**
 * Restart the fill pill whenever Splide marks a new pagination dot active.
 *
 * Splide creates the pagination during mount, after this module has injected
 * the controls, so we observe the whole scope subtree for class changes and
 * react to any pagination dot that gains `is-active`. This is what keeps the
 * pill aligned with the active slide as autoplay advances.
 *
 * @param {Element} scope Carousel-autoplay wrapper.
 */
function observeActiveDotChanges(scope) {
	if (observedActiveDotScopes.has(scope) || typeof window.MutationObserver === 'undefined') {
		return;
	}

	observedActiveDotScopes.add(scope);

	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			const target = mutation.target;

			if (
				target.nodeType !== Node.ELEMENT_NODE ||
				!target.classList.contains('splide__pagination__page') ||
				!target.classList.contains('is-active')
			) {
				return;
			}

			restartProgress(scope);
		});
	});

	observer.observe(scope, { subtree: true, attributes: true, attributeFilter: ['class'] });
}

/**
 * Mark autoplay as finished for a scope.
 *
 * Hiding the play/pause toggle is handled by a CSS state class. We also pause
 * Splide's autoplay (by clicking the toggle while it is still playing) so the
 * carousel does not keep firing its timer uselessly on the final slide.
 *
 * @param {Element} scope Carousel-autoplay wrapper.
 */
function endCarouselAutoplay(scope) {
	if (scope.classList.contains('is-autoplay-ended')) {
		return;
	}

	scope.classList.add('is-autoplay-ended');

	const toggle = scope.querySelector(toggleSelector);

	if (toggle && toggle.classList.contains('is-active')) {
		/*
		 * Routing the stop through Splide's own toggle handler is a persistent
		 * manual pause, which is exactly what we want at the end of a play
		 * through: autoplay stays stopped until the visitor presses replay.
		 */
		toggle.click();
	}
}

/**
 * Restart autoplay from the first slide for a scope.
 *
 * Works for both carousel types without the Splide instance: we resume autoplay
 * by clicking the toggle (Splide's play handler) and jump to the start by
 * clicking the first pagination dot (Splide's navigation handler).
 *
 * @param {Element} scope Carousel-autoplay wrapper.
 */
function replayAutoplay(scope) {
	scope.classList.remove('is-autoplay-ended');

	const toggle = scope.querySelector(toggleSelector);
	const firstDot = scope.querySelector('.splide__pagination .splide__pagination__page');

	if (toggle && !toggle.classList.contains('is-active')) {
		toggle.click();
	}

	if (firstDot && !firstDot.classList.contains('is-active')) {
		firstDot.click();
	}

	restartProgress(scope);
}

/**
 * Bind one-time, document-level listeners that end autoplay when the fill pill
 * finishes on the final slide.
 */
function bindAutoplayLifecycleListeners() {
	if (lifecycleListenersBound) {
		return;
	}

	lifecycleListenersBound = true;

	/*
	 * The pill fill is a CSS animation. When it completes on the last pagination
	 * dot, the play through is finished. Splide does not advance past the final
	 * slide (these carousels are `type: 'slide'`), so the pill is never
	 * restarted there and this `animationend` fires exactly once at the end.
	 */
	document.addEventListener('animationend', (event) => {
		if (event.animationName !== 'fill-animation') {
			return;
		}

		const bar = event.target;

		if (!bar.classList || !bar.classList.contains(progressBarClass)) {
			return;
		}

		const scope = resolveAutoplayScope(bar);

		if (!scope) {
			return;
		}

		const pagination = scope.querySelector('.splide__pagination');

		if (!pagination) {
			return;
		}

		const pages = pagination.querySelectorAll('.splide__pagination__page');
		const activePage = pagination.querySelector('.splide__pagination__page.is-active');

		if (pages.length && activePage && activePage === pages[pages.length - 1]) {
			endCarouselAutoplay(scope);
		}
	});
}

/**
 * Insert controls into one carousel if it needs them.
 *
 * @param {Element} carousel Carousel root element.
 */
function injectControls(carousel) {
	if (hasOwnControls(carousel)) {
		return;
	}

	const controls = createControls();

	carousel.append(controls);

	const scope = carousel.closest(autoplayScopeSelector);

	if (scope) {
		scope.style.setProperty(
			'--carousel-autoplay-interval',
			`${getAutoplayIntervalMs(carousel)}ms`
		);

		attachProgressToPagination(scope);

		const toggle = controls.querySelector(toggleSelector);
		const replay = controls.querySelector(replaySelector);

		observeAutoplayToggleState(scope, toggle);
		observeActiveDotChanges(scope);

		replay.addEventListener('click', () => replayAutoplay(scope));
	}

	scheduleAutoplayToggleAlign(scope);
}

/**
 * Force hover pause off for a PaceCore autoplay carousel before Splide mounts.
 *
 * The PaceCore block can ship `pauseOnHover: true` in its `data-splide` config.
 * Because this module runs before the plugin mounts Splide, rewriting the
 * config here is the most reliable way to honor the "no hover pause" rule for
 * carousels whose Splide instance we do not control.
 *
 * @param {Element} carousel Carousel root element.
 */
function disablePacecoreHoverPause(carousel) {
	const options = getSavedSplideOptions(carousel);

	if (!options || options.pauseOnHover === false) {
		return;
	}

	options.pauseOnHover = false;
	carousel.setAttribute('data-splide', JSON.stringify(options));
}

/**
 * Add autoplay controls to every autoplay-enabled carousel currently in the DOM.
 */
export function initCarouselAutoplayToggle() {
	bindAutoplayToggleAlignmentListeners();
	bindAutoplayLifecycleListeners();

	document.querySelectorAll(pacecoreCarouselSelector).forEach((carousel) => {
		if (!hasAutoplayPresentationScope(carousel) || !isPacecoreAutoplayCarousel(carousel)) {
			return;
		}

		disablePacecoreHoverPause(carousel);
		injectControls(carousel);
	});

	document.querySelectorAll(themeAutoplayCarouselSelector).forEach(injectControls);

	scheduleAutoplayToggleAlign(null);
}
